import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod/zod-enum";
import * as z from "zod/v4";
import { LocalDateTime, localDateTimeSchema } from "../common/zod-util";
import {
  customerGroupSchema,
  MinimalCustomerGroup,
} from "../customers/customer-group-definition";
import { emailTemplateSchema } from "../email-template";
import { MarketingChannel } from "../marketing";
import { ianaTimeZoneNameSchema } from "../marketing/timezone-list";
import { smsTemplateSchema } from "../sms-template";

export const followUpEngagementPeriodSchema = createEnum(["30", "60", "90"]);
export type FollowUpEngagementPeriod = z.infer<
  typeof followUpEngagementPeriodSchema
>;

export enum CampaignErrorCode {
  INTERNAL_ERROR = "internal_error",
  DAILY_USAGE_LIMIT_EXCEEDED = "daily_usage_limit_exceeded",
  NOT_ENABLED = "not_enabled",
}

export const followUpConfigSchema = z.object({
  enabled: z.boolean(),
  subjectLine: z.string(),
  engagementPeriod: followUpEngagementPeriodSchema,
  delayMs: z.number().int().min(0),
  originalCampaignId: zExt.objectId().optional(),
});
export type FollowUpConfig = z.infer<typeof followUpConfigSchema>;

export const ratioSchema = z.number().min(0).max(1);
export const templateVariantSchema = z.object({
  templateId: zExt.objectId(),
  abTestWeight: ratioSchema,
});
export type TemplateVariant = z.infer<typeof templateVariantSchema>;

export const campaignSchema = z.object({
  _id: zExt.objectId(),
  teamId: zExt.objectId(),
  name: z.string(),
  channel: createEnum(MarketingChannel),
  emailTemplateVariantRefs: z.array(templateVariantSchema).optional(),
  smsTemplateVariantRefs: z.array(templateVariantSchema).optional(),
  customerGroupIds: z.array(zExt.objectId()),
  excludedCustomerGroupIds: z.array(zExt.objectId()).nullable().optional(),
  scheduledAt: z.date().optional().nullable(),
  scheduledAtTimeZone: ianaTimeZoneNameSchema.nullish(),
  scheduledAtLocal: z
    .object({
      localTime: localDateTimeSchema,
      fallbackTimeZone: ianaTimeZoneNameSchema,
    })
    .nullish(),
  finishedAt: z.date().optional().nullable(),
  failedAt: z.date().optional().nullable(),
  errorCode: createEnum(CampaignErrorCode).optional(),
  followUpConfig: followUpConfigSchema.optional(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

export const outreachAnalyticsSchema = z.object({
  bounces: z.number(),
  complaints: z.number(),
  sends: z.number(),
  deliveries: z.number(),
  opens: z.number(),
  uniqueOpens: z.number(),
  clicks: z.number(),
  uniqueClicks: z.number(),
  skippedDueToUnsubscribe: z.number(),
  skippedDueToSkipConditions: z.number(),
  upsellCount: z.number(),
  upsellRevenue: z.number(),
});

export const populatedEmailTemplateVariantSchema = emailTemplateSchema.extend({
  abTestWeight: ratioSchema,
});
/** weight is from 0-1 on the backend but is shown as 0-100 on the frontend */
export type PopulatedEmailTemplateVariant = z.infer<
  typeof populatedEmailTemplateVariantSchema
>;

export const populatedSmsTemplateVariantSchema = smsTemplateSchema.extend({
  abTestWeight: ratioSchema,
});
/** weight is from 0-1 on the backend but is shown as 0-100 on the frontend */
export type PopulatedSmsTemplateVariant = z.infer<
  typeof populatedSmsTemplateVariantSchema
>;

export const populatedCampaignSchema = campaignSchema
  .omit({
    emailTemplateVariantRefs: true,
    smsTemplateVariantRefs: true,
    // customerGroupIds: true // TODO: consider omitting
  })
  .extend({
    customerGroups: z.array(customerGroupSchema).nullable(),
    excludedCustomerGroups: z.array(customerGroupSchema).nullable().optional(),
    emailTemplateVariants: z
      .array(populatedEmailTemplateVariantSchema)
      .nullable(),
    smsTemplateVariants: z.array(populatedSmsTemplateVariantSchema).nullable(),
  }) as z.ZodType<PopulatedCampaign>; // TODO (Josh) remove cast when Zod is fixed

export const templateVariantRefWithOutreachAnalyticsSchema =
  templateVariantSchema.extend({ stats: outreachAnalyticsSchema.optional() });
export type TemplateVariantRefWithOutreachAnalytics = z.infer<
  typeof templateVariantRefWithOutreachAnalyticsSchema
>;

export const campaignWithOutreachAnalyticsSchema = campaignSchema
  .omit({ emailTemplateVariantRefs: true, smsTemplateVariantRefs: true })
  .extend({
    emailTemplateVariantRefs: z
      .array(templateVariantRefWithOutreachAnalyticsSchema)
      .optional(),
    smsTemplateVariantRefs: z
      .array(templateVariantRefWithOutreachAnalyticsSchema)
      .optional(),
    stats: outreachAnalyticsSchema.optional(),
    containsInteractiveEmailBlocks: z.boolean().optional(),
  });

export type Campaign = z.infer<typeof campaignSchema>;
/**
 * A campaign with nested templates and customer groups instead of references. Used on the frontend for easier editing
 * TODO: Replace with z.infer when Zod is fixed
 */
export type PopulatedCampaign = Omit<
  Campaign,
  "emailTemplateVariantRefs" | "smsTemplateVariantRefs"
> & {
  customerGroups: MinimalCustomerGroup[] | null;
  excludedCustomerGroups?: MinimalCustomerGroup[] | null;
  emailTemplateVariants: PopulatedEmailTemplateVariant[] | null;
  smsTemplateVariants: PopulatedSmsTemplateVariant[] | null;
};
export type OutreachAnalytics = z.infer<typeof outreachAnalyticsSchema>;
export type CampaignWithOutreachAnalytics = z.infer<
  typeof campaignWithOutreachAnalyticsSchema
>;

const emailCampaignCreationParametersSchema = z.object({
  channel: z.literal(MarketingChannel.EMAIL),
  emailTemplate: z.object({
    subjectLine: z.string(),
    emailPreview: z.string(),
  }),
  name: z.string(),
  excludedCustomerGroupIds: campaignSchema.shape.excludedCustomerGroupIds,
});

const smsCampaignCreationParametersSchema = z.object({
  channel: z.literal(MarketingChannel.SMS),
  name: z.string(),
  excludedCustomerGroupIds: campaignSchema.shape.excludedCustomerGroupIds,
});

export const campaignCreationParametersSchema = z.discriminatedUnion(
  "channel",
  [emailCampaignCreationParametersSchema, smsCampaignCreationParametersSchema],
);

export type CampaignCreationParameters = z.infer<
  typeof campaignCreationParametersSchema
>;

export const campaignUpdateSchema = z.object({
  id: z.string(),
  fields: (populatedCampaignSchema as unknown as z.ZodObject<any>) // TODO (Josh) remove cast when Zod is fixed
    .omit({ _id: true, createdAt: true, updatedAt: true })
    .partial(),
});
export type CampaignUpdate = z.infer<typeof campaignUpdateSchema>;

export enum CampaignFilter {
  Status = "Status",
  Channel = "Channel",
  DateRange = "DateRange",
}

export enum CampaignStatus {
  DRAFT = "draft",
  SCHEDULED = "scheduled",
  PENDING = "pending",
  FINISHED = "finished",
  FAILED = "failed",
}

export enum CampaignChannel {
  Email = "email",
  SMS = "sms",
}

export enum CampaignSortKey {
  Name = "name",
  ScheduledAt = "scheduledAt",
  CreatedAt = "createdAt",
  UpdatedAt = "updatedAt",
  FinishedAt = "finishedAt",
}

/**
 * Check if a local time string has passed in at least one time zone.
 * Returns true if the time has passed in UTC+14 (first to reach any local time).
 */
export function hasLocalTimePassedInAnyTimeZone(
  localTime: LocalDateTime,
): boolean {
  // Parse the local time in UTC+14 (first timezone to reach any local time)
  // If this time has passed, then it has passed in at least one time zone
  const firstToReachTimeZone = new Date(localTime + "+14:00");
  return firstToReachTimeZone < new Date();
}

export function getCampaignStatus(campaign: Campaign): CampaignStatus {
  const isFailed = campaign.failedAt && campaign.failedAt < new Date();
  // if it's failed, check if it's within the last 24 hours for internal errors
  // if it's that soon, keep in pending state so we can reprocess manually
  if (isFailed) {
    const isInternalError =
      !campaign.errorCode ||
      campaign.errorCode === CampaignErrorCode.INTERNAL_ERROR;
    if (isInternalError) {
      const failedTime = campaign.failedAt?.getTime() ?? 0;
      const twentyFourHoursAgo = Date.now() - 24 * 60 * 60 * 1000;

      if (failedTime > twentyFourHoursAgo) {
        return CampaignStatus.PENDING;
      }
    }
    return CampaignStatus.FAILED;
  }
  const isFinished = campaign.finishedAt && campaign.finishedAt < new Date();
  if (isFinished) {
    return CampaignStatus.FINISHED;
  }

  // Check if campaign has started (either in normal or local time zone mode)
  const hasStarted = campaign.scheduledAt
    ? campaign.scheduledAt < new Date()
    : campaign.scheduledAtLocal
      ? hasLocalTimePassedInAnyTimeZone(campaign.scheduledAtLocal.localTime)
      : false;

  const isPending = hasStarted && !isFinished && !isFailed;
  if (isPending) {
    return CampaignStatus.PENDING;
  }

  // Campaign is scheduled if it has a future scheduledAt or scheduledAtLocal that hasn't started yet
  const isScheduled =
    (campaign.scheduledAt && campaign.scheduledAt > new Date()) ||
    (campaign.scheduledAtLocal && !hasStarted);
  if (isScheduled) {
    return CampaignStatus.SCHEDULED;
  }

  if (!isFinished && !isPending && !isScheduled && !isFailed) {
    return CampaignStatus.DRAFT;
  }
  return CampaignStatus.DRAFT;
}

export function hasCampaignStarted(campaign: Campaign): boolean {
  return campaign.scheduledAt
    ? campaign.scheduledAt < new Date()
    : campaign.scheduledAtLocal
      ? hasLocalTimePassedInAnyTimeZone(campaign.scheduledAtLocal.localTime)
      : false;
}

export function campaignWorkflowId(campaignId: string) {
  return `send-campaign-${campaignId}`;
}

export function followUpCampaignWorkflowId(campaignId: string) {
  return `create-follow-up-campaign-${campaignId}`;
}
